import { UPDropdown } from 'components/dropdown';
import UPInput from 'components/input';
import UPSwitch from 'components/switch';
import { useTranslation } from 'react-i18next';
import {
  TIME_REGISTRATION_POLICY__FIELD_AFTER_START,
  TIME_REGISTRATION_POLICY__FIELD_AUTH_MODE,
  TIME_REGISTRATION_POLICY__FIELD_BEFORE_START,
  TIME_REGISTRATION_POLICY__FIELD_CHOOSE_DIRECTION,
  TIME_REGISTRATION_POLICY__FIELD_INTERVAL,
  TIME_REGISTRATION_POLICY__FIELD_MAX_OVERWORK,
  TIME_REGISTRATION_POLICY__FIELD_ON_SITE_RADIUS,
  TIME_REGISTRATION_SETTINGS__METERS_UNIT,
  TIME_REGISTRATION_SETTINGS__MINUTES_UNIT,
} from 'translations/constants';
import { KioskAuthenticationMode } from 'types/company/kiosk/IKioskSettings';
import { TimeRegistrationRoundingDirection } from 'types/company/time-registration/ITimeRegistrationSettings';
import { FieldErrors, UnitValue, UnitValueField } from 'types/time-registration-policy/ITimeRegistrationPolicy';
import { AUTH_MODE_LABEL_KEYS, DIRECTION_LABEL_KEYS } from './helpers';
import { FieldsBlock, NarrowField, RoundingSideLabel, SwitchLabel, SwitchRow, TwoColumnGrid } from './styles';
import { UnitDefinition } from './units';

interface PolicyUnitFieldsProps {
  def: UnitDefinition;
  value: UnitValue;
  errors: FieldErrors;
  onChange: (patch: Partial<UnitValue>) => void;
  disabled?: boolean;
}

interface NumberInputConfig {
  field: UnitValueField;
  label: string;
  unit: string;
  min: number;
  max: number;
  testId: string;
  placeholder?: string;
  required?: boolean;
}

const PolicyUnitFields = ({ def, value, errors, onChange, disabled }: PolicyUnitFieldsProps): JSX.Element => {
  const [t] = useTranslation();
  const minutesUnit = t(TIME_REGISTRATION_SETTINGS__MINUTES_UNIT);

  const errorText = (field: UnitValueField): string => {
    const error = errors[field];
    return error ? t(error.key, error.params) : '';
  };

  const renderNumber = ({ field, label, unit, min, max, testId, placeholder, required }: NumberInputConfig) => (
    <UPInput
      type="number"
      emptyAsNull
      label={label}
      placeholder={placeholder ?? ''}
      required={required}
      unit={unit}
      min={min}
      max={max}
      step={1}
      value={(value[field] as number) ?? ''}
      onChange={(next: number | null) => onChange({ [field]: next })}
      error={!!errors[field]}
      helperText={errorText(field)}
      disabled={disabled}
      testId={testId}
    />
  );

  const renderSwitch = (label: string) => (
    <SwitchRow>
      <SwitchLabel>{label}</SwitchLabel>
      <UPSwitch
        checked={!!value.enabled}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ enabled: event.target.checked })}
        disabled={disabled}
        testId="tr-field-toggle"
      />
    </SwitchRow>
  );

  const directionItems = Object.values(TimeRegistrationRoundingDirection).map(direction => ({
    label: t(DIRECTION_LABEL_KEYS[direction]),
    value: direction,
  }));

  const renderRoundingSide = (side: 'start' | 'end', labelKey: string) => {
    const directionField: UnitValueField = side === 'start' ? 'startDirection' : 'endDirection';
    const minutesField: UnitValueField = side === 'start' ? 'startIntervalMinutes' : 'endIntervalMinutes';
    return (
      <div key={side}>
        <RoundingSideLabel>{t(labelKey)}</RoundingSideLabel>
        <TwoColumnGrid>
          <UPDropdown
            label=""
            items={directionItems}
            value={(value[directionField] as string) ?? ''}
            placeholder={t(TIME_REGISTRATION_POLICY__FIELD_CHOOSE_DIRECTION)}
            onChange={(event: { target: { value: string } }) =>
              onChange({ [directionField]: event.target.value || null })
            }
            error={!!errors[directionField]}
            helperText={errorText(directionField)}
            disabled={disabled}
            testId={`tr-field-${side}-direction`}
          />
          {renderNumber({
            field: minutesField,
            label: '',
            unit: minutesUnit,
            min: 1,
            max: 1440,
            placeholder: t(TIME_REGISTRATION_POLICY__FIELD_INTERVAL),
            testId: `tr-field-${side}-minutes`,
          })}
        </TwoColumnGrid>
      </div>
    );
  };

  const authModeItems = [
    KioskAuthenticationMode.OpenList,
    KioskAuthenticationMode.Pin,
    KioskAuthenticationMode.PinPlusPersonalDeviceApproval,
  ].map(mode => ({
    label: t(AUTH_MODE_LABEL_KEYS[mode]),
    value: String(mode),
  }));

  switch (def.kind) {
    case 'toggle':
      return <FieldsBlock>{renderSwitch(t(def.nameKey))}</FieldsBlock>;
    case 'window':
      return (
        <TwoColumnGrid>
          {renderNumber({
            field: 'beforeMinutes',
            label: t(TIME_REGISTRATION_POLICY__FIELD_BEFORE_START),
            unit: minutesUnit,
            min: 0,
            max: 1440,
            testId: 'tr-field-before',
          })}
          {renderNumber({
            field: 'afterMinutes',
            label: t(TIME_REGISTRATION_POLICY__FIELD_AFTER_START),
            unit: minutesUnit,
            min: 0,
            max: 1440,
            testId: 'tr-field-after',
          })}
        </TwoColumnGrid>
      );
    case 'number':
      return (
        <NarrowField>
          {renderNumber({
            field: 'maximum',
            label: t(def.nameKey),
            unit: '',
            min: 0,
            max: 10,
            testId: 'tr-field-number',
          })}
        </NarrowField>
      );
    case 'authMode':
      return (
        <UPDropdown
          label={t(TIME_REGISTRATION_POLICY__FIELD_AUTH_MODE)}
          items={authModeItems}
          value={value.authenticationMode ? String(value.authenticationMode) : ''}
          onChange={(event: { target: { value: string } }) =>
            onChange({ authenticationMode: event.target.value ? Number(event.target.value) : null })
          }
          error={!!errors.authenticationMode}
          helperText={errorText('authenticationMode')}
          disabled={disabled}
          testId="tr-field-select"
        />
      );
    case 'rounding':
      return (
        <FieldsBlock>
          {renderSwitch(t(def.toggleLabelKey))}
          {value.enabled && renderRoundingSide('start', def.startLabelKey)}
          {value.enabled && renderRoundingSide('end', def.endLabelKey)}
        </FieldsBlock>
      );
    case 'overwork':
      return (
        <FieldsBlock>
          {renderSwitch(t(def.toggleLabelKey))}
          {value.enabled && (
            <NarrowField>
              {renderNumber({
                field: 'maxOverworkMinutes',
                label: t(TIME_REGISTRATION_POLICY__FIELD_MAX_OVERWORK),
                unit: minutesUnit,
                min: 0,
                max: 1440,
                required: true,
                testId: 'tr-field-max-overwork',
              })}
            </NarrowField>
          )}
        </FieldsBlock>
      );
    case 'location':
      return (
        <FieldsBlock>
          {renderSwitch(t(def.toggleLabelKey))}
          {value.enabled && (
            <NarrowField>
              {renderNumber({
                field: 'onSiteRadiusMeters',
                label: t(TIME_REGISTRATION_POLICY__FIELD_ON_SITE_RADIUS),
                unit: t(TIME_REGISTRATION_SETTINGS__METERS_UNIT),
                min: 1,
                max: 10000,
                required: true,
                testId: 'tr-field-radius',
              })}
            </NarrowField>
          )}
        </FieldsBlock>
      );
    default:
      return null;
  }
};

PolicyUnitFields.defaultProps = {
  disabled: false,
};

export default PolicyUnitFields;
